/**
 * Scroll to a comment card and flash it.
 *
 * The card owns resolution, mention state and the full text; every other
 * surface that references a comment (the in-document highlights, the video
 * timeline markers) is a pointer to it rather than a copy. This is the one
 * way those pointers navigate. The reverse direction — card to content —
 * lives in commentAnchors.tsx.
 *
 * Extracted from ConsultationsPanel on 2026-08-20 when the in-document
 * highlights gained the same affordance — the original was a local function,
 * and a second hand-rolled copy would have been free to drift on the flash
 * duration, the scroll block, or the restore-the-previous-shadow detail
 * below, which is the part that is easy to get wrong.
 *
 * SCOPED TO THE CARD'S OWN SCROLLER (2026-08-26). The cards moved into the
 * viewer's rail, which is a sticky column with its own inner scroller.
 * `scrollIntoView` scrolls EVERY scrollable ancestor, so clicking a marker
 * scrolled the rail — correct — and then scrolled the page as well, dragging
 * the document out from under the marker the reader had just clicked. When
 * the card sits inside a scroller we move that scroller alone; a card in
 * normal page flow still gets plain scrollIntoView.
 *
 * The flash writes `style.boxShadow` directly and restores whatever was there
 * before. That matters: a card that mentions the viewer already carries an
 * inline ring, and a naive reset to '' would silently strip it.
 */

function scrollParent(el: HTMLElement): HTMLElement | null {
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const style = window.getComputedStyle(cur);
    const overflowY = style.overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && cur.scrollHeight > cur.clientHeight) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return null;
}

/**
 * Fired before the scroll so the rail can make the card reachable.
 *
 * The cards live in the rail now, and the rail can be collapsed, on another
 * tab, or showing the row collapsed. Scrolling to an element that is not
 * rendered does nothing at all — silently — which is precisely the failure a
 * reviewer would report as "clicking the marker does nothing". The panel
 * listens, opens itself to the right tab and expands the row; the scroll
 * below then runs against a card that exists.
 */
export const OPEN_COMMENT_EVENT = 'cellcohub:open-comment';

export function openComment(commentId: string): void {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_COMMENT_EVENT, { detail: { commentId } }));
  } catch {
    // A browser without CustomEvent still gets the scroll below when the card
    // happens to be rendered already.
  }
  // One frame for the panel to render its response, then scroll. Re-queried
  // after the wait on purpose: before it, the element may not exist yet.
  window.setTimeout(() => scrollToCard(commentId), 60);
}

function scrollToCard(commentId: string): void {
  const el = document.getElementById(`comment-${commentId}`);
  if (!el) return;

  const scroller = scrollParent(el);
  if (scroller) {
    // Centre the card in its own scroller without touching the page scroll.
    const top = el.offsetTop - scroller.offsetTop
      - (scroller.clientHeight / 2) + (el.offsetHeight / 2);
    scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  } else {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  const prior = el.style.boxShadow;
  el.style.boxShadow = '0 0 0 3px #ffc107';
  el.style.transition = 'box-shadow 0.3s';
  setTimeout(() => { el.style.boxShadow = prior; }, 2200);
}
